import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { MatchmakerLayout } from "@/components/matchmaker/MatchmakerLayout";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { MatchPairCard } from "@/components/matchmaker/MatchPairCard";
import { ProfileDetailModal } from "@/components/matchmaker/ProfileDetailModal";
import { WeightedCompatibilityScore } from "@/components/matchmaker/WeightedCompatibilityScore";
import { MatchCreationConfirmDialog } from "@/components/matchmaker/MatchCreationConfirmDialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { supabase } from "@/integrations/supabase/client";
import { calculateWeightedCompatibility } from "@/utils/weightedCompatibility";
import { RefreshCw, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ScrollArea } from "@/components/ui/scroll-area";
import { getScoreColor } from "@/utils/compatibilityColors";
import { cn } from "@/lib/utils";
import { useUserRole } from "@/hooks/useUserRole";
import { anonymizeProfile } from "@/utils/anonymization";

interface MatchSuggestion {
  id: string;
  profile_1_id: string;
  profile_2_id: string;
  compatibility_score: number;
  compatibility_details: any;
  dismissed_by_matchmaker: boolean;
  match_created: boolean;
  match_id?: string;
  profile1?: any;
  profile2?: any;
  compatibilityResult?: any;
}

export default function MutualMatches() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAdmin } = useUserRole();
  const [suggestions, setSuggestions] = useState<MatchSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [minScore, setMinScore] = useState([70]);
  const [showDismissed, setShowDismissed] = useState(false);
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [selectedProfile, setSelectedProfile] = useState<any | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [expandedCompatibility, setExpandedCompatibility] = useState<Record<string, boolean>>({});
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingMatch, setPendingMatch] = useState<{ profile1Id: string; profile2Id: string; profile1Name: string; profile2Name: string } | null>(null);
  // Guard so auto-generate fires at most once per mount. Without this, an empty
  // generation result re-triggers the effect via fetchSuggestions → setSuggestions([]).
  const autoGenAttempted = useRef(false);

  useEffect(() => {
    const initializeSuggestions = async () => {
      await fetchSuggestions();
    };
    initializeSuggestions();
  }, [showDismissed]);

  // Auto-generate suggestions if none exist (once per mount)
  useEffect(() => {
    if (!loading && suggestions.length === 0 && !generating && !autoGenAttempted.current) {
      autoGenAttempted.current = true;
      generateSuggestions();
    }
  }, [loading, suggestions.length]);

  const fetchSuggestions = async () => {
    setLoading(true);
    try {
      const { data: user } = await supabase.auth.getUser();

      let query = supabase
        .from("match_suggestions")
        .select("*")
        .order("compatibility_score", { ascending: false });

      if (!showDismissed) {
        query = query.eq("dismissed_by_matchmaker", false);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Fetch dismissed_matches for cross-view consistency
      let dismissedPairs: Set<string> = new Set();
      if (user.user) {
        const { data: dismissedData } = await supabase
          .from("dismissed_matches")
          .select("profile_1_id, profile_2_id")
          .eq("dismissed_by_matchmaker_id", user.user.id);

        dismissedData?.forEach((dismissed) => {
          dismissedPairs.add(`${dismissed.profile_1_id}:${dismissed.profile_2_id}`);
        });
      }

      // Fetch full profile details and recalculate compatibility fresh
      const suggestionsWithProfiles = await Promise.all(
        (data || []).map(async (suggestion) => {
          const [profile1Response, profile2Response] = await Promise.all([
            supabase.from("profiles").select("*").eq("id", suggestion.profile_1_id).single(),
            supabase.from("profiles").select("*").eq("id", suggestion.profile_2_id).single(),
          ]);

          // Recalculate compatibility with current algorithm and fresh data
          // For admins, anonymize profiles in compatibility calculation
          const freshCompatibility = isAdmin
            ? calculateWeightedCompatibility(
                anonymizeProfile(profile1Response.data),
                anonymizeProfile(profile2Response.data)
              )
            : calculateWeightedCompatibility(
                profile1Response.data,
                profile2Response.data
              );

          // Check if this pair is dismissed in dismissed_matches table
          const isDismissedInTable = dismissedPairs.has(`${suggestion.profile_1_id}:${suggestion.profile_2_id}`);

          return {
            ...suggestion,
            profile1: profile1Response.data,
            profile2: profile2Response.data,
            compatibility_score: freshCompatibility.score, // Update with fresh score
            compatibilityResult: freshCompatibility, // Use fresh calculation
            dismissed_by_matchmaker: suggestion.dismissed_by_matchmaker || isDismissedInTable, // Combine both sources
          };
        })
      );

      setSuggestions(suggestionsWithProfiles);
    } catch (error) {
      console.error("mutual_matches.fetch_suggestions_failed");
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch match suggestions",
      });
    } finally {
      setLoading(false);
    }
  };

  const generateSuggestions = async () => {
    setGenerating(true);
    try {
      // Fetch all active profiles
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("*")
        .eq("is_active", true)
        .eq("profile_completed", true);

      if (profilesError) throw profilesError;

      const maleProfiles = profiles.filter((p) => p.gender === "male");
      const femaleProfiles = profiles.filter((p) => p.gender === "female");

      const newSuggestions: any[] = [];
      const updatedSuggestions: any[] = [];
      let updatedCount = 0;

      // Calculate weighted compatibility for all male-female pairs
      for (const male of maleProfiles) {
        for (const female of femaleProfiles) {
          const compatibility = calculateWeightedCompatibility(male, female);

          // Only create/update suggestions for mutual matches with 70%+ compatibility
          if (compatibility.score >= 70 && !compatibility.dealBreakerFailed) {
            // Check if suggestion already exists
            const { data: existing } = await supabase
              .from("match_suggestions")
              .select("id")
              .eq("profile_1_id", male.id)
              .eq("profile_2_id", female.id)
              .maybeSingle();

            if (existing) {
              // Update existing suggestion with new compatibility data
              const { error: updateError } = await supabase
                .from("match_suggestions")
                .update({
                  compatibility_score: compatibility.score,
                  compatibility_details: compatibility as any,
                })
                .eq("id", existing.id);

              if (!updateError) {
                updatedCount++;
              }
            } else {
              // Add to new suggestions
              newSuggestions.push({
                profile_1_id: male.id,
                profile_2_id: female.id,
                compatibility_score: compatibility.score,
                compatibility_details: compatibility as any,
              });
            }
          }
        }
      }

      // Insert new suggestions
      if (newSuggestions.length > 0) {
        const { error: insertError } = await supabase
          .from("match_suggestions")
          .insert(newSuggestions);

        if (insertError) throw insertError;
      }

      // Show results
      if (newSuggestions.length > 0 || updatedCount > 0) {
        const messages = [];
        if (newSuggestions.length > 0) {
          messages.push(`${newSuggestions.length} new`);
        }
        if (updatedCount > 0) {
          messages.push(`${updatedCount} updated`);
        }
        toast({
          title: "Success",
          description: `Match suggestions: ${messages.join(", ")}`,
        });
      } else {
        toast({
          title: "No changes",
          description: "All match suggestions are up to date",
        });
      }

      await fetchSuggestions();
    } catch (error) {
      console.error("mutual_matches.generate_suggestions_failed");
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to generate match suggestions",
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleDismiss = async (profile1Id: string, profile2Id: string) => {
    try {
      const { data: user } = await supabase.auth.getUser();

      // Update match_suggestions table
      const { error: suggestionError } = await supabase
        .from("match_suggestions")
        .update({ dismissed_by_matchmaker: true })
        .eq("profile_1_id", profile1Id)
        .eq("profile_2_id", profile2Id);

      if (suggestionError) throw suggestionError;

      // Also insert into dismissed_matches table for cross-view consistency
      if (user.user) {
        const { error: dismissError } = await supabase
          .from("dismissed_matches")
          .insert({
            dismissed_by_matchmaker_id: user.user.id,
            profile_1_id: profile1Id,
            profile_2_id: profile2Id,
          });

        // Ignore unique constraint violations (already dismissed)
        if (dismissError && !dismissError.message.includes('duplicate key')) {
          throw dismissError;
        }
      }

      toast({
        title: "Match dismissed",
        description: "This suggestion has been dismissed",
      });

      await fetchSuggestions();
    } catch (error) {
      console.error("mutual_matches.dismiss_failed");
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to dismiss match",
      });
    }
  };

  const handleUndismiss = async (profile1Id: string, profile2Id: string) => {
    try {
      const { data: user } = await supabase.auth.getUser();

      // Update match_suggestions table
      const { error: suggestionError } = await supabase
        .from("match_suggestions")
        .update({ dismissed_by_matchmaker: false })
        .eq("profile_1_id", profile1Id)
        .eq("profile_2_id", profile2Id);

      if (suggestionError) throw suggestionError;

      // Also remove from dismissed_matches table
      if (user.user) {
        await supabase
          .from("dismissed_matches")
          .delete()
          .eq("dismissed_by_matchmaker_id", user.user.id)
          .eq("profile_1_id", profile1Id)
          .eq("profile_2_id", profile2Id);
      }

      toast({
        title: "Match restored",
        description: "This suggestion has been restored",
      });

      await fetchSuggestions();
    } catch (error) {
      console.error("mutual_matches.restore_failed");
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to restore match",
      });
    }
  };

  const handleCreateMatch = (profile1Id: string, profile2Id: string) => {
    // Find the profiles to get their names
    const suggestion = suggestions.find(
      s => s.profile_1_id === profile1Id && s.profile_2_id === profile2Id
    );

    if (!suggestion) return;

    // Open confirmation dialog
    setPendingMatch({
      profile1Id,
      profile2Id,
      profile1Name: suggestion.profile1?.first_name || "Profile 1",
      profile2Name: suggestion.profile2?.first_name || "Profile 2",
    });
    setConfirmDialogOpen(true);
  };

  const confirmCreateMatch = async () => {
    if (!pendingMatch) return;

    try {
      const { data, error } = await supabase
        .from("matches")
        .insert({
          profile_1_id: pendingMatch.profile1Id,
          profile_2_id: pendingMatch.profile2Id,
          created_by_matchmaker_id: (await supabase.auth.getUser()).data.user?.id,
          status: "pending",
        })
        .select()
        .single();

      if (error) throw error;

      // Update suggestion to mark as match created
      await supabase
        .from("match_suggestions")
        .update({ match_created: true, match_id: data.id })
        .eq("profile_1_id", pendingMatch.profile1Id)
        .eq("profile_2_id", pendingMatch.profile2Id);

      toast({
        title: "Match created",
        description: "The match has been successfully created",
      });

      setConfirmDialogOpen(false);
      setPendingMatch(null);
      await fetchSuggestions();
    } catch (error) {
      console.error("mutual_matches.create_match_failed");
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to create match",
      });
    }
  };

  const handleViewProfile = (profileId: string) => {
    if (isAdmin) {
      // For admins, find the profile and anonymize it
      let foundProfile = null;
      for (const suggestion of suggestions) {
        if (suggestion.profile1?.id === profileId) {
          foundProfile = anonymizeProfile(suggestion.profile1);
          break;
        }
        if (suggestion.profile2?.id === profileId) {
          foundProfile = anonymizeProfile(suggestion.profile2);
          break;
        }
      }
      setSelectedProfile(foundProfile);
      setSelectedProfileId(profileId);
    } else {
      // For matchmakers, just use the profile ID
      setSelectedProfileId(profileId);
      setSelectedProfile(null);
    }
    setModalOpen(true);
  };

  const toggleCompatibilityDetails = (suggestionId: string) => {
    setExpandedCompatibility(prev => ({
      ...prev,
      [suggestionId]: !prev[suggestionId]
    }));
  };

  // Anonymize suggestions for admins
  const displaySuggestions = isAdmin
    ? suggestions.map(s => ({
        ...s,
        profile1: s.profile1 ? anonymizeProfile(s.profile1) : null,
        profile2: s.profile2 ? anonymizeProfile(s.profile2) : null,
      }))
    : suggestions;

  const filteredSuggestions = displaySuggestions.filter(
    (s) => s.compatibility_score >= minScore[0] && !s.match_created
  );

  const Layout = isAdmin ? AdminLayout : MatchmakerLayout;
  const layoutTitle = isAdmin ? "Mutual Matches (Anonymized)" : "Mutual Matches";

  return (
    <Layout title={layoutTitle}>
      <div className="container mx-auto p-6 max-w-7xl">
        {/* Welcome Banner */}
        <div className="mb-8 rounded-2xl bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10 p-6 border border-primary/20 shadow-lg">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-3xl">🎯</span>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              AI-Powered Match Suggestions
            </h2>
          </div>
          <p className="text-foreground/70">
            Review compatibility scores and create matches between highly compatible profiles. Let's find perfect pairs! ✨
          </p>
        </div>

        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">Mutual Matches</h1>
              <p className="text-muted-foreground">
                {isAdmin
                  ? "Pre-calculated compatible pairs (data anonymized for privacy)"
                  : "Pre-calculated compatible pairs ready for matching"
                }
              </p>
            </div>
            <Button onClick={generateSuggestions} disabled={generating}>
              {generating ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              {generating ? "Generating..." : "Refresh Suggestions"}
            </Button>
          </div>
        </div>

        <Card className="p-4 mb-6">
          <div className="space-y-4">
            <div>
              <Label>Minimum Compatibility Score: {minScore[0]}%</Label>
              <Slider
                value={minScore}
                onValueChange={setMinScore}
                min={50}
                max={100}
                step={5}
                className="mt-2"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="show-dismissed"
                checked={showDismissed}
                onCheckedChange={setShowDismissed}
              />
              <Label htmlFor="show-dismissed">Show dismissed matches</Label>
            </div>
          </div>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : filteredSuggestions.length === 0 ? (
          <Alert>
            <AlertTitle>No mutual matches found</AlertTitle>
            <AlertDescription>
              Click "Refresh Suggestions" to generate new match suggestions, or lower the minimum
              compatibility score
            </AlertDescription>
          </Alert>
        ) : (
          <ScrollArea className="h-[calc(100vh-400px)]">
            <div className="space-y-4">
              {filteredSuggestions.map((suggestion) => (
                <div key={suggestion.id} className="space-y-3">
                  <MatchPairCard
                    profile1={suggestion.profile1}
                    profile2={suggestion.profile2}
                    compatibilityResult={suggestion.compatibilityResult}
                    onCreateMatch={handleCreateMatch}
                    onDismiss={handleDismiss}
                    onUndismiss={handleUndismiss}
                    onViewProfile={handleViewProfile}
                    isDismissed={suggestion.dismissed_by_matchmaker}
                  />
                  <Collapsible
                    open={expandedCompatibility[suggestion.id]}
                    onOpenChange={() => toggleCompatibilityDetails(suggestion.id)}
                  >
                    <Card className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className={cn("text-2xl font-bold", getScoreColor(suggestion.compatibilityResult.score))}>
                            {suggestion.compatibilityResult.score}%
                          </span>
                          <span className="text-sm text-muted-foreground ml-2">
                            Compatibility Explanation
                          </span>
                        </div>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm">
                            {expandedCompatibility[suggestion.id] ? "Hide Details" : "Show Details"}
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent className="mt-4">
                        <WeightedCompatibilityScore
                          result={suggestion.compatibilityResult}
                          showDetails={true}
                          profile1Name={suggestion.profile1?.first_name}
                          profile2Name={suggestion.profile2?.first_name}
                        />
                      </CollapsibleContent>
                    </Card>
                  </Collapsible>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </div>

      <ProfileDetailModal
        profileId={selectedProfileId}
        profile={selectedProfile}
        open={modalOpen}
        onOpenChange={setModalOpen}
        anonymized={isAdmin}
        isAdmin={isAdmin}
      />

      <MatchCreationConfirmDialog
        open={confirmDialogOpen}
        onOpenChange={setConfirmDialogOpen}
        onConfirm={confirmCreateMatch}
        profile1Name={pendingMatch?.profile1Name || ""}
        profile2Name={pendingMatch?.profile2Name || ""}
        isAdmin={isAdmin}
      />
    </Layout>
  );
}
